// P:\0PJ\synforge\frontend\app\settings\tokens\page.tsx v11 2026.07.31 - History grouped by meeting session: meeting_consume entries now group under one row per session (click to expand), non-meeting entries (signup/refund/recharge/admin) stay as individual rows

"use client"

import { useState, useEffect } from "react"
import { Coins, ArrowUpRight, ArrowDownRight, SlidersHorizontal, Plus, CreditCard, ChevronDown } from "lucide-react"
import { billingAPI } from "@/lib/api"

interface TokenTxn {
  id: string
  amount: number
  balance_after: number
  reason: string
  memo: string
  created_at: string
  session_id?: string | null
  session_agenda?: string | null
}

interface SavedCard {
  id: string
  brand: string
  last4: string
  is_default: boolean
}

const REASON_META: Record<string, { label: string; color: string }> = {
  signup_grant:    { label: "Signup Bonus",      color: "#22D3EE" },
  meeting_consume: { label: "Meeting",           color: "#F472B6" },
  admin_adjust:    { label: "Admin Adjustment",  color: "#A78BFA" },
  refund:          { label: "Refund",            color: "#34D399" },
  recharge:        { label: "Card Recharge",     color: "#FBBF24" },
}

function normalizeCards(raw: any): SavedCard[] {
  if (Array.isArray(raw)) return raw
  if (raw && Array.isArray(raw.results)) return raw.results
  return []
}

// History 렌더 항목 — 회의(meeting_consume)는 세션 단위로 묶인 그룹, 그 외는 개별 거래.
type HistoryRow =
  | { kind: "meeting_group"; sessionId: string; agenda: string; latestAt: string; total: number; items: TokenTxn[] }
  | { kind: "single"; txn: TokenTxn }

// 소수점 2자리 고정 표시 - Synforge 토큰 잔액/거래가 이제 DecimalField 라 소수점을 가질 수 있다.
function fmtTok(n: number | null | undefined): string {
  if (n === null || n === undefined) return "-"
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function buildHistoryRows(txns: TokenTxn[]): HistoryRow[] {
  const rows: HistoryRow[] = []
  const groups: Record<string, HistoryRow & { kind: "meeting_group" }> = {}

  for (const t of txns) {
    if (t.reason === "meeting_consume" && t.session_id) {
      const key = t.session_id
      if (!groups[key]) {
        const group: HistoryRow & { kind: "meeting_group" } = {
          kind: "meeting_group",
          sessionId: key,
          agenda: t.session_agenda || "Meeting",
          latestAt: t.created_at,
          total: 0,
          items: [],
        }
        groups[key] = group
        rows.push(group)
      }
      const g = groups[key]
      g.total += t.amount
      g.items.push(t)
      if (new Date(t.created_at) > new Date(g.latestAt)) g.latestAt = t.created_at
    } else {
      rows.push({ kind: "single", txn: t })
    }
  }

  // 최신순 정렬 (그룹은 latestAt, 단일 항목은 created_at 기준)
  rows.sort((a, b) => {
    const at = a.kind === "meeting_group" ? a.latestAt : a.txn.created_at
    const bt = b.kind === "meeting_group" ? b.latestAt : b.txn.created_at
    return new Date(bt).getTime() - new Date(at).getTime()
  })

  return rows
}

export default function TokensPage() {
  const [balance, setBalance] = useState<number | null>(null)
  const [txns, setTxns] = useState<TokenTxn[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null)

  const [showRecharge, setShowRecharge] = useState(false)
  const [amount, setAmount] = useState("10")

  const [cards, setCards] = useState<SavedCard[]>([])
  const [cardsLoading, setCardsLoading] = useState(false)
  const [cardsError, setCardsError] = useState("")
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)
  const [methodOpen, setMethodOpen] = useState(false)

  const [tokenValueUsd, setTokenValueUsd] = useState<number | null>(null)
  const [avgMeetingUsd, setAvgMeetingUsd] = useState<number | null>(null)

  const [busy, setBusy] = useState(false)
  const [payError, setPayError] = useState("")

  const loadTokens = () => {
    billingAPI.myTokens()
      .then((res) => {
        setBalance(res.data.balance)
        setTxns(res.data.transactions || [])
      })
      .catch((err) => console.error("Failed to load tokens:", err))
      .finally(() => setLoading(false))
  }

  useEffect(() => { loadTokens() }, [])

  const loadCards = () => {
    setCardsLoading(true)
    setCardsError("")
    billingAPI.getPaymentMethods()
      .then((res) => {
        const list = normalizeCards(res.data)
        setCards(list)
        const def = list.find((c) => c.is_default)
        setSelectedCardId(def ? def.id : (list[0]?.id ?? null))
      })
      .catch((err) => {
        console.error("Failed to load payment methods:", err)
        setCardsError(err?.response?.status
          ? `Failed to load cards (HTTP ${err.response.status}).`
          : "Failed to load cards. Check network connection.")
      })
      .finally(() => setCardsLoading(false))
  }

  const loadPricingInfo = () => {
    billingAPI.getPricing()
      .then((res) => setTokenValueUsd(res.data.token_value_usd))
      .catch((err) => console.error("Failed to load pricing:", err))

    billingAPI.getUsageSummary()
      .then((res) => {
        const byMeeting = res.data.by_meeting || []
        if (byMeeting.length === 0) return
        const totalUsd = byMeeting.reduce((sum: number, m: any) => sum + (m.total_usd || 0), 0)
        const avgUsd = totalUsd / byMeeting.length
        setAvgMeetingUsd(avgUsd)
      })
      .catch((err) => console.error("Failed to load usage summary:", err))
  }

  const openRecharge = () => {
    setAmount("10")
    setSelectedCardId(null)
    setPayError("")
    setMethodOpen(false)
    setShowRecharge(true)
    loadCards()
    loadPricingInfo()
  }

  const closeModal = () => {
    setShowRecharge(false)
  }

  const [paySuccess, setPaySuccess] = useState("")

  const handlePay = async () => {
    if (!selectedCardId || amountNum <= 0) return
    setBusy(true)
    setPayError("")
    setPaySuccess("")
    try {
      const res = await billingAPI.rechargeTokens(amountNum, selectedCardId)
      setPaySuccess(`Success! ${res.data.tokens_added.toLocaleString()} tokens added.`)
      loadTokens()  // 잔액 + History 즉시 갱신
      setTimeout(() => { setShowRecharge(false); setPaySuccess("") }, 1800)
    } catch (err: any) {
      setPayError(err?.response?.data?.message || "Payment failed. Please try again.")
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-64">
        <p className="text-white/30 text-sm">Loading...</p>
      </div>
    )
  }

  const selectedCard = cards.find((c) => c.id === selectedCardId) || null
  const amountNum = parseFloat(amount) || 0
  const tokensForAmount = tokenValueUsd ? Math.floor(amountNum / tokenValueUsd) : null
  const avgTokensPerMeetingCalc = (tokenValueUsd && avgMeetingUsd !== null)
    ? Math.round(avgMeetingUsd / tokenValueUsd)
    : null

  const historyRows = buildHistoryRows(txns)

  return (
    <div className="relative p-8 max-w-[760px] overflow-hidden">
      <div
        className="absolute -top-24 -left-16 w-[420px] h-[420px] rounded-full opacity-30 blur-[100px] pointer-events-none"
        style={{ background: "linear-gradient(135deg, #06B6DA 0%, #02D6E8 50%, #22D3EE 100%)" }}
      />

      <div className="relative">
        <p className="text-[10px] font-semibold text-white/25 tracking-[0.2em] uppercase mb-8">
          Tokens
        </p>

        <div className="mb-10">
          <div className="flex items-end justify-between mb-1">
            <div className="flex items-baseline gap-3">
              <span
                className="text-6xl font-bold tracking-tight"
                style={{
                  backgroundImage: "linear-gradient(120deg, #FFFFFF 0%, #7DD3E8 55%, #02D6E8 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {(balance ?? 0).toLocaleString()}
              </span>
              <span className="text-lg text-white/40 font-medium pb-1">tokens</span>
            </div>
            <button
              onClick={openRecharge}
              className="flex items-center gap-1.5 text-sm font-medium px-4 py-2.5 rounded-full text-white transition-transform hover:scale-[1.03]"
              style={{ background: "linear-gradient(120deg, #06B6DA, #22D3EE)" }}
            >
              <Plus size={14} />
              Recharge
            </button>
          </div>
          <p className="text-xs text-white/30">Current balance</p>
        </div>

        <div className="h-px bg-gradient-to-r from-white/10 via-white/[0.04] to-transparent mb-8" />

        <div>
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-semibold text-white/80">History</h3>
            <SlidersHorizontal size={13} className="text-white/20" />
          </div>

          {historyRows.length === 0 && (
            <p className="text-white/25 text-sm py-8 text-center">Nothing yet — your history will show up here.</p>
          )}

          <div className="space-y-0">
            {historyRows.map((row) => {
              if (row.kind === "single") {
                const t = row.txn
                const meta = REASON_META[t.reason] || { label: t.reason, color: "#94A3B8" }
                const isPositive = t.amount >= 0
                return (
                  <div
                    key={t.id}
                    className="group flex items-center gap-4 py-3.5 border-b border-white/[0.04] last:border-0"
                  >
                    <div
                      className="w-[3px] h-9 rounded-full shrink-0"
                      style={{ background: meta.color, opacity: 0.7 }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white/90">{meta.label}</p>
                      <p className="text-[11px] text-white/25">
                        {new Date(t.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {isPositive
                        ? <ArrowUpRight size={13} className="text-white/30" />
                        : <ArrowDownRight size={13} className="text-white/30" />}
                      <span className={`text-sm font-medium tabular-nums ${isPositive ? "text-white" : "text-white/60"}`}>
                        {isPositive ? "+" : ""}{fmtTok(t.amount)}
                      </span>
                    </div>
                    <span className="text-[11px] text-white/20 tabular-nums w-20 text-right shrink-0">
                      {fmtTok(t.balance_after)}
                    </span>
                  </div>
                )
              }

              // 회의 그룹
              const meta = REASON_META["meeting_consume"]
              const isExpanded = expandedGroup === row.sessionId
              const lastBalance = row.items[0]?.balance_after
              return (
                <div key={row.sessionId} className="border-b border-white/[0.04] last:border-0">
                  <button
                    onClick={() => setExpandedGroup(isExpanded ? null : row.sessionId)}
                    className="w-full group flex items-center gap-4 py-3.5 text-left hover:bg-white/[0.02] transition-colors"
                  >
                    <div
                      className="w-[3px] h-9 rounded-full shrink-0"
                      style={{ background: meta.color, opacity: 0.7 }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white/90 truncate">{row.agenda}</p>
                      <p className="text-[11px] text-white/25">
                        {new Date(row.latestAt).toLocaleString()} · {row.items.length} charge{row.items.length > 1 ? "s" : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <ArrowDownRight size={13} className="text-white/30" />
                      <span className="text-sm font-medium tabular-nums text-white/60">
                        {fmtTok(row.total)}
                      </span>
                    </div>
                    <span className="text-[11px] text-white/20 tabular-nums w-20 text-right shrink-0">
                      {lastBalance !== undefined ? fmtTok(lastBalance) : ""}
                    </span>
                    <ChevronDown size={13} className={`text-white/20 transition-transform shrink-0 ${isExpanded ? "rotate-180" : ""}`} />
                  </button>

                  {isExpanded && (
                    <div className="pl-4 pb-3 space-y-1">
                      {row.items.map((it) => (
                        <div key={it.id} className="flex items-center justify-between text-[11px] py-1">
                          <span className="text-white/30">{new Date(it.created_at).toLocaleTimeString()}</span>
                          <span className="text-white/50 tabular-nums">{fmtTok(it.amount)} tokens</span>
                          <span className="text-white/20 tabular-nums w-16 text-right">{fmtTok(it.balance_after)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {showRecharge && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={closeModal}
        >
          <div
            className="relative w-[340px] rounded-2xl p-6 overflow-hidden"
            style={{ background: "#0d0d14", border: "1px solid rgba(255,255,255,0.08)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="absolute -top-12 -right-12 w-40 h-40 rounded-full opacity-40 blur-[60px] pointer-events-none"
              style={{ background: "linear-gradient(135deg, #06B6DA, #22D3EE)" }}
            />
            <div className="relative">
              <div className="flex items-center gap-2 mb-4">
                <Coins size={16} className="text-cyan-300" />
                <span className="text-sm font-semibold text-white">Recharge Tokens</span>
              </div>

              <label className="block text-xs font-medium text-white/50 mb-1.5">Amount (USD)</label>
              <div className="relative mb-1.5">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
                <input
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                  className="w-full bg-[#13131f] border border-white/[0.08] rounded-xl pl-7 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-cyan-500/60 transition-colors"
                  placeholder="10.00"
                />
              </div>

              {tokensForAmount !== null && (
                <p className="text-[11px] text-cyan-300/70 mb-4">
                  ≈ {tokensForAmount.toLocaleString()} Synforge tokens
                </p>
              )}

              <div className="flex gap-2 mb-5">
                {[10, 25, 50, 100].map((v) => (
                  <button
                    key={v}
                    onClick={() => setAmount(String(v))}
                    className="flex-1 py-1.5 text-xs rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-white/60 transition-colors"
                  >
                    ${v}
                  </button>
                ))}
              </div>

              {avgTokensPerMeetingCalc !== null && (
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl px-3.5 py-2.5 mb-5">
                  <p className="text-[11px] text-white/50 leading-relaxed">
                    Your average meeting uses about <span className="text-white/80 font-medium">{avgTokensPerMeetingCalc.toLocaleString()} tokens</span>
                    {tokensForAmount !== null && (
                      <> — this recharge covers roughly <span className="text-white/80 font-medium">{Math.floor(tokensForAmount / avgTokensPerMeetingCalc)}</span> meeting(s).</>
                    )}
                  </p>
                </div>
              )}

              <label className="block text-xs font-medium text-white/50 mb-1.5">Payment method</label>

              {cardsLoading ? (
                <p className="text-white/30 text-xs py-3">Loading cards...</p>
              ) : cardsError ? (
                <p className="text-[11px] text-red-400 mb-4">{cardsError}</p>
              ) : cards.length === 0 ? (
                <p className="text-xs text-white/50 leading-relaxed mb-4">
                  No payment method on file. Add a card in the Billing tab first.
                </p>
              ) : (
                <div className="relative mb-4">
                  <button
                    onClick={() => setMethodOpen((v) => !v)}
                    className="w-full flex items-center gap-3 bg-[#13131f] border border-white/[0.08] rounded-xl px-3.5 py-2.5 hover:border-white/20 transition-colors"
                  >
                    <CreditCard size={15} className="text-white/40 shrink-0" />
                    <span className="text-xs text-white/80 flex-1 text-left">
                      {selectedCard ? `${selectedCard.brand.toUpperCase()} •••• ${selectedCard.last4}` : "Select a card"}
                    </span>
                    <ChevronDown size={14} className={`text-white/30 transition-transform ${methodOpen ? "rotate-180" : ""}`} />
                  </button>

                  {methodOpen && (
                    <div className="absolute left-0 right-0 mt-1.5 bg-[#13131f] border border-white/[0.08] rounded-xl overflow-hidden z-10 shadow-2xl">
                      {cards.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => { setSelectedCardId(c.id); setMethodOpen(false) }}
                          className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-left hover:bg-white/[0.05] transition-colors ${
                            selectedCardId === c.id ? "bg-cyan-500/[0.06]" : ""
                          }`}
                        >
                          <CreditCard size={14} className="text-white/40 shrink-0" />
                          <span className="text-xs text-white/80 flex-1">
                            {c.brand.toUpperCase()} •••• {c.last4}
                          </span>
                          {c.is_default && <span className="text-[10px] text-cyan-300/70">Default</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {selectedCard && amountNum > 0 && (
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl px-3.5 py-3 mb-4">
                  <p className="text-[11px] text-white/50 leading-relaxed">
                    ${amountNum.toFixed(2)} will be charged to {selectedCard.brand.toUpperCase()} •••• {selectedCard.last4}.
                    Your token balance will be credited immediately once the payment succeeds.
                  </p>
                </div>
              )}

              <button
                onClick={handlePay}
                disabled={!amount || amountNum <= 0 || !selectedCardId || busy}
                className="w-full py-2.5 rounded-lg text-xs font-medium text-white transition-transform hover:scale-[1.01] disabled:opacity-30"
                style={{ background: "linear-gradient(120deg, #06B6DA, #22D3EE)" }}
              >
                {busy ? "Processing..." : `Pay $${amount}`}
              </button>

              {payError && <p className="text-[11px] text-red-400 mt-3">{payError}</p>}
              {paySuccess && <p className="text-[11px] text-emerald-400 mt-3">{paySuccess}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}